import { useState } from 'react'
import {
  Alert,
  Divider,
  Grid,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core'
import { Ticket } from '@data/tickets/model/ticket'
import {
  AlertTriangle,
  Battery,
  ChevronRight,
  Droplets,
  LayoutGrid,
  List,
  TrendingDown,
  TrendingUp,
  Wifi,
  Zap,
} from 'lucide-react'

interface DashboardStatsProps {
  tickets: Ticket[];
  onStatClick?: (status: string | null) => void;
  activeFilter?: string | null;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
}


export function DashboardStats({
                                 tickets,
                                 onStatClick,
                                 activeFilter,
                                 viewMode,
                                 onViewModeChange,
                               }: DashboardStatsProps) {
  const totalTickets = tickets.filter((t) => t.status !== 'closed').length
  const openTickets = tickets.filter((t) => t.status === 'open').length
  const inProgressTickets = tickets.filter((t) => t.status === 'in_progress').length
  const pendingReviewTickets = tickets.filter((t) => t.status === 'pending_review').length
  // const resolvedTickets = tickets.filter((t) => t.status === 'closed').length;
  const criticalTickets = tickets.filter((t) => t.priority === 'high' && t.status === 'open').length

  // Calculate ticket counts by issue type
  const noFlowTickets = tickets.filter((t) => t.issueType === 'no_flow' && t.status !== 'closed').length
  const noFlowOpen = tickets.filter((t) => t.issueType === 'no_flow' && t.status === 'open').length
  const noFlowInProgress = tickets.filter((t) => t.issueType === 'no_flow' && t.status === 'in_progress').length
  const connectivityTickets = tickets.filter((t) => t.issueType === 'connectivity' && t.status !== 'closed').length
  const connectivityOpen = tickets.filter((t) => t.issueType === 'connectivity' && t.status === 'open').length
  const connectivityInProgress = tickets.filter((t) => t.issueType === 'connectivity' && t.status === 'in_progress').length
  const batteryTickets = tickets.filter((t) => t.issueType === 'battery' && t.status !== 'closed').length
  const batteryOpen = tickets.filter((t) => t.issueType === 'battery' && t.status === 'open').length
  const batteryInProgress = tickets.filter((t) => t.issueType === 'battery' && t.status === 'in_progress').length
  const powerTickets = tickets.filter((t) => t.issueType === 'power' && t.status !== 'closed').length
  const powerOpen = tickets.filter((t) => t.issueType === 'power' && t.status === 'open').length
  const powerInProgress = tickets.filter((t) => t.issueType === 'power' && t.status === 'in_progress').length

  // Calculate priority counts for each status
  const getPriorityCounts = (statusFilter: string) => {
    let filteredTickets = tickets
    if (statusFilter !== 'all') {
      filteredTickets = tickets.filter((t) => t.status === statusFilter)
    }

    return {
      normal: filteredTickets.filter((t) => t.priority === 'normal').length,
      high: filteredTickets.filter((t) => t.priority === 'high').length,
    }
  }

  // Calculate priority counts for each issue type
  const getPriorityCountsByIssueType = (issueType: string) => {
    const filteredTickets = tickets.filter((t) => t.issueType === issueType)

    return {
      normal: filteredTickets.filter((t) => t.priority === 'normal').length,
      high: filteredTickets.filter((t) => t.priority === 'high').length,
    }
  }

  const stats = [
    {
      label: 'Total Tickets',
      value: totalTickets,
      icon: TrendingUp,
      color: '#228be6',
      change: '+12%',
      trend: 'up',
      filterValue: 'all',
      priorities: getPriorityCounts('all'),
    },
    {
      label: 'Open Tickets',
      value: openTickets,
      icon: AlertTriangle,
      color: '#fd7e14',
      change: '-5%',
      trend: 'down',
      filterValue: 'open',
      priorities: getPriorityCounts('open'),
    },
    {
      label: 'In Progress',
      value: inProgressTickets,
      icon: TrendingUp,
      color: '#7950f2',
      change: '+8%',
      trend: 'up',
      filterValue: 'in_progress',
      priorities: getPriorityCounts('in_progress'),
    },
    {
      label: 'Pending Review',
      value: pendingReviewTickets,
      icon: TrendingUp,
      color: '#7950f2',
      change: '+8%',
      trend: 'up',
      filterValue: 'pending_review',
      priorities: getPriorityCounts('pending_review'),
    },
    // {
    //   label: 'Resolved',
    //   value: resolvedTickets,
    //   icon: CheckCircle2,
    //   color: '#40c057',
    //   change: '+23%',
    //   trend: 'up',
    //   filterValue: 'resolved',
    //   priorities: getPriorityCounts('resolved'),
    // },
  ]

  const issueTypeStats = [
    {
      label: 'No Flow',
      value: noFlowTickets,
      open: noFlowOpen,
      inProgress: noFlowInProgress,
      icon: Droplets,
      color: '#1971c2',
      issueType: 'no_flow',
      priorities: getPriorityCountsByIssueType('no_flow'),
    },
    {
      label: 'Offline',
      value: connectivityTickets,
      open: connectivityOpen,
      inProgress: connectivityInProgress,
      icon: Wifi,
      color: '#0ca678',
      issueType: 'connectivity',
      priorities: getPriorityCountsByIssueType('connectivity'),
    },
    {
      label: 'Low Battery',
      value: batteryTickets,
      open: batteryOpen,
      inProgress: batteryInProgress,
      icon: Battery,
      color: '#f59f00',
      issueType: 'battery',
      priorities: getPriorityCountsByIssueType('battery'),
    },
    {
      label: 'Power Off',
      value: powerTickets,
      open: powerOpen,
      inProgress: powerInProgress,
      icon: Zap,
      color: '#e03131',
      issueType: 'power',
      priorities: getPriorityCountsByIssueType('power'),
    },
  ]

  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())

  const toggleCard = (issueType: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev)
      next.has(issueType) ? next.delete(issueType) : next.add(issueType)
      return next
    })
  }

  return (
    <Stack gap='sm' mb='xs'>
      {/* Stats + Controls — single unified row, stacks below lg */}
      <Grid gap='xs' align='stretch'>

        {/* Left: Status Stats — inner 4-column grid (1 card per col) */}
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Grid gap='sm'>
            {stats.map((stat, index) => {
              const isActive = activeFilter === stat.filterValue
              return (
                <Grid.Col key={index} span={{ base: 6, lg: 3 }}>
                  <Paper
                    shadow='sm'
                    p='md'
                    radius='md'
                    withBorder
                    style={{
                      cursor: onStatClick ? 'pointer' : 'default',
                      height: '100%',
                      transform: isActive ? 'scale(1.02)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                      outline: isActive ? `2px solid ${stat.color}` : 'none',
                      outlineOffset: isActive ? '2px' : '0',
                    }}
                    onClick={() => onStatClick && onStatClick(stat.filterValue)}
                  >
                    <Group justify='space-between' mb='xs'>
                      <div
                        style={{
                          padding: '0.5rem',
                          borderRadius: '0.5rem',
                          backgroundColor: stat.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <stat.icon size={20} color='white' />
                      </div>
                      <Group gap={4}>
                        {stat.trend === 'up' ? (
                          <TrendingUp size={16} color='green' />
                        ) : (
                          <TrendingDown size={16} color='red' />
                        )}
                        <Text size='xs' c={stat.trend === 'up' ? 'green' : 'red'} fw={500}>
                          {stat.change}
                        </Text>
                      </Group>
                    </Group>
                    <Group>
                      <Text size='md' c='dimmed'>{stat.label}</Text>
                      <Text size='md' fw={400}>{stat.value}</Text>
                    </Group>
                  </Paper>
                </Grid.Col>
              )
            })}
          </Grid>
        </Grid.Col>

        {/* Divider 1 — hidden when stacked */}
        <Grid.Col span='content' visibleFrom='md'>
          <Divider orientation='vertical' h='100%' />
        </Grid.Col>

        {/* Center: Issue Type Stats — 2×2 inner grid */}
        <Grid.Col span={{ base: 12, lg: 'auto' }}>
          <Grid gap='sm'>
            {issueTypeStats.map((stat) => {
              const isActive = activeFilter === stat.issueType
              const isExpanded = expandedCards.has(stat.issueType)

              return (
                <Grid.Col key={stat.issueType} span={6}>
                  <Paper
                    shadow='sm'
                    p='md'
                    radius='md'
                    withBorder
                    style={{
                      cursor: onStatClick ? 'pointer' : 'default',
                      height: '100%',
                      transform: isActive ? 'scale(1.02)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                      outline: isActive ? `2px solid ${stat.color}` : 'none',
                      outlineOffset: isActive ? '2px' : '0',
                    }}
                    onClick={() => onStatClick && onStatClick(stat.issueType)}
                  >
                    <Group justify='space-between' mb='xs'>
                      <div
                        style={{
                          padding: '0.5rem',
                          borderRadius: '0.5rem',
                          backgroundColor: stat.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <stat.icon size={20} color='white' />
                      </div>
                    </Group>

                    <Group
                      gap='xs'
                      // wrap='nowrap'
                    >
                      <Text size='xs'>{stat.label}</Text>
                      <Text size='xs' fw={400}>{stat.value}</Text>

                      <ChevronRight
                        size={14}
                        style={{
                          flexShrink: 0,
                          color: 'var(--mantine-color-dimmed)',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                          cursor: 'pointer',
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          toggleCard(stat.issueType)
                        }}
                      />

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          overflow: 'hidden',
                          maxWidth: isExpanded ? 300 : 0,
                          opacity: isExpanded ? 1 : 0,
                          transition: 'max-width 0.25s ease, opacity 0.2s ease',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Divider orientation='vertical' />
                        <Text size='xs' c='dimmed'>Open</Text>
                        <Text size='xs' fw={400}>{stat.open}</Text>
                        <Divider orientation='vertical' />
                        <Text size='xs' c='dimmed'>In Progress</Text>
                        <Text size='xs' fw={400}>{stat.inProgress}</Text>
                      </div>
                    </Group>
                  </Paper>
                </Grid.Col>
              )
            })}
          </Grid>
        </Grid.Col>

        {/* Divider 2 — hidden when stacked */}
        <Grid.Col span='content' visibleFrom='md'>
          <Divider orientation='vertical' h='100%' />
        </Grid.Col>

        {/* Right: View toggle + critical alert */}
        <Grid.Col span={{ base: 12, md: 'content' }}>
          <Stack
            gap='sm'
            h='100%'
          >
            <SegmentedControl
              value={viewMode}
              onChange={onViewModeChange}
              data={[
                {
                  label: (
                    <Group gap='xs' wrap='nowrap'>
                      <List size={16} />
                      <Text size='sm'>List</Text>
                    </Group>
                  ),
                  value: 'list',
                },
                {
                  label: (
                    <Group gap='xs' wrap='nowrap'>
                      <LayoutGrid size={16} />
                      <Text size='sm'>Category</Text>
                    </Group>
                  ),
                  value: 'grid',
                },
              ]}
            />
            {criticalTickets > 0 ? (
              <Alert
                variant='light'
                color='red'
                icon={<AlertTriangle size={16} />}
              >
                <Text size='sm'>
                  <Text component='span' fw={600}>
                    {criticalTickets} High priority {criticalTickets === 1 ? 'issue' : 'issues'}
                  </Text>{' '}
                  requires immediate attention
                </Text>
              </Alert>
            ) : null}
          </Stack>
        </Grid.Col>

      </Grid>
    </Stack>
  )
}
